import {
  Building2,
  ClipboardCheck,
  Cpu,
  FileAudio,
  FileSearch,
  Gauge,
  GraduationCap,
  ListTree,
  Network,
  PanelsTopLeft,
  Radio,
  RotateCcwKey,
  SlidersHorizontal,
  UserCircle,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { matchPath } from 'react-router-dom';

import type { UserRole } from '@/types/auth';
import {
  consoleRoutePaths,
  type ConsoleRouteId,
  type ConsoleRoutePath,
} from './consoleRoutePaths';

export type ConsoleNavigationDomainId =
  | 'operate'
  | 'improve'
  | 'manage'
  | 'account'
  | 'platform'
  | 'lab_tools';

export type ConsoleCanonicalOwnerId =
  | 'control'
  | 'devices'
  | 'onboarding'
  | 'investigations'
  | 'models'
  | 'people'
  | 'monitoring_policy'
  | 'radio_gateways'
  | 'profile'
  | 'organizations'
  | 'platform_health'
  | 'platform_audit'
  | 'acoustic_domains'
  | 'browser_audio_lab';

export type ConsoleRouteAccess =
  | 'all'
  | 'workspace'
  | 'admin'
  | 'internal'
  | 'owner'
  | 'lab'
  | 'development';

export interface ConsoleRouteGroup {
  id: ConsoleNavigationDomainId;
  label: string;
  description: string;
  /**
   * When set, the top bar collects this group's pages in one menu with this
   * name, and the console uses the name wherever it shows the group.
   */
  menuLabel?: string;
}

export interface ConsoleRouteLink {
  id: ConsoleRouteId;
  label: string;
  shortLabel?: string;
  path: ConsoleRoutePath;
  groupId: ConsoleNavigationDomainId;
  canonicalOwnerId: ConsoleCanonicalOwnerId;
  description: string;
  keywords?: readonly string[];
  access: ConsoleRouteAccess;
  icon: LucideIcon;
  primary?: boolean;
  discoverable?: boolean;
}

export interface ConsoleRouteContext {
  userRole?: UserRole | null;
  isOwner: boolean;
  isOrganizationScope?: boolean;
  enableLabRoutes: boolean;
}

export const consoleCanonicalOwnerLabels: Record<
  ConsoleCanonicalOwnerId,
  string
> = {
  control: 'Overview',
  devices: 'Devices',
  onboarding: 'Device setup',
  investigations: 'Device alarms',
  models: 'Models',
  people: 'People',
  monitoring_policy: 'Monitoring policy',
  radio_gateways: 'Radio microphones',
  profile: 'Your profile',
  organizations: 'Organizations',
  platform_health: 'System health',
  platform_audit: 'Audit log',
  acoustic_domains: 'Acoustic domains',
  browser_audio_lab: 'Browser audio lab',
};

export const consoleRouteGroups: ConsoleRouteGroup[] = [
  {
    id: 'operate',
    label: 'Operate',
    description: 'Triage, devices, setup, and connected investigation work.',
  },
  {
    id: 'improve',
    label: 'Improve',
    description: 'Evidence, training, model review, assignment, and verification.',
  },
  {
    id: 'manage',
    label: 'Manage',
    description: 'Organization people and monitoring policy.',
  },
  {
    id: 'account',
    label: 'Account',
    description: 'Signed-in user self-service.',
  },
  {
    id: 'platform',
    label: 'Platform',
    description: 'Cross-tenant administration and platform evidence.',
    menuLabel: 'SoundOps',
  },
  {
    id: 'lab_tools',
    label: 'Lab tools',
    description: 'Development-only recovery and browser diagnostics.',
  },
];

export function getConsoleRouteGroup(id: ConsoleNavigationDomainId): ConsoleRouteGroup {
  const group = consoleRouteGroups.find((candidate) => candidate.id === id);
  if (!group) throw new Error(`Unknown console route group: ${id}`);
  return group;
}

/** The name people see for a group: its menu name when it has one. */
export function getConsoleRouteGroupLabel(group: ConsoleRouteGroup): string {
  return group.menuLabel ?? group.label;
}

export const consoleRoutes: ConsoleRouteLink[] = [
  {
    id: 'control', label: 'Overview', path: consoleRoutePaths.control,
    groupId: 'operate', canonicalOwnerId: 'control', access: 'workspace',
    description: 'Monitor detection readiness, processing, and saved results.',
    keywords: ['detection', 'readiness', 'processing', 'anomalies'],
    icon: PanelsTopLeft,
    primary: true,
  },
  {
    id: 'devices', label: 'Devices', path: consoleRoutePaths.devices,
    groupId: 'operate', canonicalOwnerId: 'devices', access: 'workspace',
    description: 'See this organization\'s devices and open permitted device work.',
    keywords: ['inventory', 'machine', 'sensor', 'status'], icon: Building2,
    primary: true,
  },
  {
    id: 'onboard', label: 'Device setup', path: consoleRoutePaths.onboard,
    groupId: 'operate', canonicalOwnerId: 'onboarding', access: 'admin',
    description: 'Resume the exact persisted setup run selected from Devices.',
    keywords: ['onboard', 'readiness', 'workflow'], icon: ClipboardCheck,
    discoverable: false,
  },
  {
    id: 'investigations', label: 'Device alarms',
    path: consoleRoutePaths.investigations,
    groupId: 'operate', canonicalOwnerId: 'investigations', access: 'workspace',
    description: 'Review saved alarms and their audio for one device.',
    keywords: ['alarm', 'anomaly', 'clip', 'history'],
    icon: FileSearch,
  },
  {
    id: 'file-detection', label: 'Analyze an audio file',
    path: consoleRoutePaths['file-detection'], groupId: 'operate',
    canonicalOwnerId: 'investigations', access: 'internal',
    description: 'Start an investigation from registry-backed file inference.',
    keywords: ['detect', 'upload', 'offline'], icon: FileAudio,
  },
  {
    id: 'models', label: 'Models', path: consoleRoutePaths.models,
    groupId: 'improve', canonicalOwnerId: 'models', access: 'internal',
    description: 'Review model versions, evidence, assignment, and live-use state.',
    keywords: ['registry', 'deploy', 'promote'], icon: Cpu,
  },
  {
    id: 'training', label: 'Train a model', path: consoleRoutePaths.training,
    groupId: 'improve', canonicalOwnerId: 'models', access: 'internal',
    description: 'Controlled training subflow opened from the Models lifecycle.',
    keywords: ['dataset', 'wizard', 'candidate'], icon: GraduationCap,
    discoverable: false,
  },
  {
    id: 'users', label: 'People', path: consoleRoutePaths.users,
    groupId: 'manage', canonicalOwnerId: 'people', access: 'admin',
    description: 'Manage human access within the active organization scope.',
    keywords: ['users', 'roles', 'deactivate'], icon: Users, primary: true,
  },
  {
    id: 'surveillance-policies', label: 'Monitoring policy',
    path: consoleRoutePaths['surveillance-policies'], groupId: 'manage',
    canonicalOwnerId: 'monitoring_policy', access: 'admin',
    description: 'Configure monitoring defaults, readiness, and notifications.',
    keywords: ['surveillance', 'alerts', 'channels'], icon: SlidersHorizontal,
    primary: true,
  },
  {
    id: 'radio', label: 'Radio microphones', path: consoleRoutePaths.radio,
    groupId: 'manage', canonicalOwnerId: 'radio_gateways', access: 'internal',
    description: 'Commission wireless microphones: enrollment, grants and signing readiness.',
    keywords: ['radio', 'wireless', 'gateway', 'enrollment', 'grant'], icon: Radio,
  },
  {
    id: 'profile', label: 'Your profile', path: consoleRoutePaths.profile,
    groupId: 'account', canonicalOwnerId: 'profile', access: 'all',
    description: 'Maintain your identity, password, and security settings.',
    keywords: ['account', 'password', 'security'], icon: UserCircle,
  },
  {
    id: 'owner-organizations', label: 'Organizations',
    path: consoleRoutePaths['owner-organizations'], groupId: 'platform',
    canonicalOwnerId: 'organizations', access: 'owner',
    description: 'Add customer organizations and manage their limits.',
    keywords: ['tenants', 'quota'], icon: Building2, primary: true,
  },
  {
    id: 'owner-users', label: 'People', path: consoleRoutePaths['owner-users'],
    groupId: 'platform', canonicalOwnerId: 'people', access: 'owner',
    description: 'Manage who can sign in, in every organization.',
    keywords: ['users', 'roles', 'accounts'], icon: Users, primary: true,
  },
  {
    id: 'owner-system-health', label: 'System health', shortLabel: 'Health',
    path: consoleRoutePaths['owner-system-health'], groupId: 'platform',
    canonicalOwnerId: 'platform_health', access: 'owner',
    description: 'See how SoundOps services are running for all organizations.',
    keywords: ['api', 'workers', 'services'], icon: Gauge, primary: true,
  },
  {
    id: 'owner-audit-log', label: 'Audit log',
    path: consoleRoutePaths['owner-audit-log'], groupId: 'platform',
    canonicalOwnerId: 'platform_audit', access: 'owner',
    description: 'See who made important changes, and when.',
    keywords: ['events', 'actor', 'history'], icon: ListTree, primary: true,
  },
  {
    id: 'owner-acoustic-domains', label: 'Acoustic domains',
    path: consoleRoutePaths['owner-acoustic-domains'], groupId: 'platform',
    canonicalOwnerId: 'acoustic_domains', access: 'owner',
    description: 'Group machines that sound alike so they can share alarm thresholds.',
    keywords: ['acoustic', 'association', 'evidence', 'thresholds'], icon: Network,
    primary: true,
  },
  {
    id: 'browser-mic-stream', label: 'Transport check',
    path: consoleRoutePaths['browser-mic-stream'], groupId: 'lab_tools',
    canonicalOwnerId: 'browser_audio_lab', access: 'lab',
    description: 'Check non-persisted browser audio transport.',
    keywords: ['microphone', 'stream'], icon: Radio, primary: true,
  },
  {
    id: 'reset-usb', label: 'Reset recovery',
    path: consoleRoutePaths['reset-usb'], groupId: 'lab_tools',
    canonicalOwnerId: 'onboarding', access: 'development',
    description: 'Create an exact-host enrollment reset request for development hardware.',
    keywords: ['reset', 'recovery', 'usb', 'factory'], icon: RotateCcwKey,
    primary: true,
  },
];

const hasOrganizationScope = (context: ConsoleRouteContext) =>
  !context.isOwner || context.isOrganizationScope === true;

export function canSeeConsoleRoute(
  route: ConsoleRouteLink,
  context: ConsoleRouteContext,
): boolean {
  const isAdmin = context.userRole === 'ADMIN' || context.userRole === 'SUPERADMIN';

  switch (route.access) {
    case 'all':
      return true;
    case 'workspace':
      return hasOrganizationScope(context);
    case 'admin':
      return isAdmin && hasOrganizationScope(context);
    case 'internal':
      return isAdmin && context.enableLabRoutes && hasOrganizationScope(context);
    case 'owner':
      return context.isOwner && !context.isOrganizationScope;
    case 'lab':
      return (
        context.enableLabRoutes &&
        context.userRole !== 'DEVICE' &&
        hasOrganizationScope(context)
      );
    case 'development':
      return (
        context.enableLabRoutes &&
        context.userRole != null &&
        context.userRole !== 'DEVICE'
      );
  }
}

export function getVisibleConsoleRoutes(
  context: ConsoleRouteContext,
): ConsoleRouteLink[] {
  return consoleRoutes.filter((route) =>
    canSeeConsoleRoute(route, context),
  );
}

export function getConsoleRouteById(id: ConsoleRouteId): ConsoleRouteLink {
  const route = consoleRoutes.find((candidate) => candidate.id === id);
  if (!route) throw new Error(`Unknown console route id: ${id}`);
  return route;
}

export function getConsoleRouteForPath(pathname: string): ConsoleRouteLink | null {
  return [...consoleRoutes]
    .sort((left, right) => right.path.length - left.path.length)
    .find(
      (route) =>
        pathname === route.path || pathname.startsWith(`${route.path}/`),
    ) ?? null;
}

const PRODUCT_NAME = 'SoundOps';

/**
 * Browser tab title for a console path: "<page> · <organization> · SoundOps".
 * Route patterns with parameters (e.g. /devices/:deviceId/setup) match too.
 */
export function getConsolePageTitle(pathname: string, organizationName: string | null): string {
  const route =
    [...consoleRoutes]
      .sort((left, right) => right.path.length - left.path.length)
      .find((candidate) => matchPath({ path: candidate.path, end: true }, pathname)) ??
    getConsoleRouteForPath(pathname);
  return [route?.label, organizationName, PRODUCT_NAME].filter(Boolean).join(' · ');
}

export function getPrimaryConsoleRoutes(
  context: ConsoleRouteContext,
): ConsoleRouteLink[] {
  return getVisibleConsoleRoutes(context).filter(
    (route) => route.primary && route.discoverable !== false,
  );
}

export function getVisibleConsoleSections(context: ConsoleRouteContext) {
  const visibleRoutes = getPrimaryConsoleRoutes(context);
  return consoleRouteGroups
    .map((group) => ({
      ...group,
      routes: visibleRoutes.filter((route) => route.groupId === group.id),
    }))
    .filter((group) => group.routes.length > 0);
}

export function searchConsoleRoutes(
  context: ConsoleRouteContext,
  query: string,
): ConsoleRouteLink[] {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const routes = getPrimaryConsoleRoutes(context);
  if (!normalizedQuery) return routes;

  return routes.filter((route) => {
    const group = getConsoleRouteGroup(route.groupId);
    const searchText = [
      route.label,
      route.description,
      consoleCanonicalOwnerLabels[route.canonicalOwnerId],
      group.label,
      group.menuLabel,
      ...(route.keywords ?? []),
    ]
      .join(' ')
      .toLocaleLowerCase();
    return searchText.includes(normalizedQuery);
  });
}

/** Device search follows the Devices page rule: whoever can open Devices can find a device. */
export function canSearchConsoleDevices(context: ConsoleRouteContext): boolean {
  return canSeeConsoleRoute(getConsoleRouteById('devices'), context);
}

/** How many page names the search placeholder offers, keeping it short enough to read. */
const SEARCH_PLACEHOLDER_PAGE_EXAMPLES = { withDevices: 1, pagesOnly: 2 } as const;
const orList = new Intl.ListFormat('en', { style: 'long', type: 'disjunction' });

/** Search placeholder built from what this person can actually find. */
export function getConsoleSearchPlaceholder(context: ConsoleRouteContext): string {
  const canFindDevices = canSearchConsoleDevices(context);
  const pageExamples = getPrimaryConsoleRoutes(context)
    .filter((route) => route.id !== 'devices')
    .slice(
      0,
      canFindDevices
        ? SEARCH_PLACEHOLDER_PAGE_EXAMPLES.withDevices
        : SEARCH_PLACEHOLDER_PAGE_EXAMPLES.pagesOnly,
    )
    .map((route) => route.label);
  const pageHint = pageExamples.length > 0 ? `a page like ${orList.format(pageExamples)}` : null;
  if (canFindDevices) {
    return pageHint ? `Device name or ID, or ${pageHint}` : 'Device name or ID';
  }
  return pageHint ? `Try ${pageHint}` : 'Type a page name';
}

export function getConsoleLandingPath(context: ConsoleRouteContext): ConsoleRoutePath {
  if (context.isOwner && !context.isOrganizationScope) {
    return consoleRoutePaths['owner-organizations'];
  }
  return consoleRoutePaths.devices;
}
